import { type } from "arktype";

import { CustomFieldValues } from "@/api/document/custom-field-values";
import type { SignatureRequirement } from "@/types/signatures";

const patternItem = type({
	itemId: "string > 0",
	values: "Record<string, string | undefined>",
});

export const SignaturePreparationSchemas = {
	form: type({
		deadlineDays: "1 <= number.integer <= 365",
		customValues: "Record<string, string | undefined>",
		patternValues: type.Record("string", patternItem.array().or("undefined")),
	}),
};

export type SignaturePreparationForm = typeof SignaturePreparationSchemas.form.infer;

export const SignaturePreparation = {
	fieldKey(termId: number, fieldId: number) {
		return `${termId}_${fieldId}`;
	},

	patternKey(termId: number, patternId: number) {
		return `${termId}_${patternId}`;
	},

	items(patternValues: SignaturePreparationForm["patternValues"], key: string) {
		if (!Object.hasOwn(patternValues, key)) {
			return [];
		}

		return patternValues[key];
	},

	values(
		requirement: SignatureRequirement | undefined,
		customValues: Record<string, string | undefined>,
	) {
		return (
			requirement?.fields.map((field) => {
				const value = CustomFieldValues.canonical(
					field.inputType,
					customValues[this.fieldKey(requirement.termId, field.fieldId)] ?? "",
				);

				if (value === null) {
					throw new Error(`invalid_custom_field:${field.fieldId}`);
				}

				return { fieldId: field.fieldId, value };
			}) ?? []
		);
	},

	valid(field: SignatureRequirement["fields"][number], value: string | undefined) {
		return CustomFieldValues.canonical(field.inputType, value ?? "") !== null;
	},

	patternItems(
		requirement: SignatureRequirement,
		patternValues: SignaturePreparationForm["patternValues"],
	) {
		return requirement.patterns.map((pattern) => ({
			patternId: pattern.patternId,
			items: this.items(patternValues, this.patternKey(requirement.termId, pattern.patternId)).map(
				(item) => ({
					id: item.itemId,
					values: pattern.fields.map((field) => {
						const value = CustomFieldValues.canonical(
							field.inputType,
							item.values[String(field.fieldId)] ?? "",
						);

						if (value === null) {
							throw new Error(`invalid_pattern_field:${pattern.patternId}:${field.fieldId}`);
						}

						return { fieldId: field.fieldId, value };
					}),
				}),
			),
		}));
	},

	patternComplete(
		pattern: SignatureRequirement["patterns"][number],
		items: SignaturePreparationForm["patternValues"][string],
	) {
		if (items.length < pattern.minimumItems) {
			return false;
		}

		return items.every((item) =>
			pattern.fields.every((field) => this.valid(field, item.values[String(field.fieldId)])),
		);
	},
};
